import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, setAccessToken, clearAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=not auth, object=auth
  const [error, setError] = useState("");

  // On mount, rehydrate user state from the httpOnly cookie that the
  // browser sends automatically with `withCredentials: true`. No token is
  // read from localStorage — the cookie is the source of truth.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  async function login(email, password) {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Admin-provisioned account on first login — pass the change-password
      // token up; do not set user state so RoleRoute does not auto-route them.
      if (data?.must_change_password) return data;
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    clearAccessToken();
    setUser(false);
  }

  async function refreshUser() {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }

  return (
    <AuthContext.Provider value={{ user, error, login, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
